import { Environment } from "./Environment";
import { IMessageFilter } from "../interfaces/IMessageFilter";

import DateTimeService from "./DateTimeService";
import { IMessageResult } from "../interfaces/IMessageResult";
import { IMessage } from "../interfaces/IMessage";
import { ISPEvent } from "../interfaces/ISPEvent";
import { Helper } from '../Helper';
import { FetchProxy } from "intraactive-sdk-helper";
import { sdkContainerInstance } from "../global/Container";
import { IHttpStatus } from "../interfaces/IHttpStatus";
import { IEventPeriod } from "../interfaces/IEventPeriod";
import { IEnvironment } from "../interfaces/IEnvironment";

export default class EventService {

  private static convertSPEventToMessage(sPEvent: ISPEvent): IMessage {
    return {
      id: sPEvent.id ? parseInt(sPEvent.id, 10) : undefined,
      title: sPEvent.title,
      content: sPEvent.content,
      image: sPEvent.image,
      startDate: sPEvent.startDate,
      allDayEvent: sPEvent.allDayEvent,
      endDate: sPEvent.endDate,
      author: Helper.syncUserProperties(sPEvent.author),
      likes: [],
      commentCount: 0,
      categories: sPEvent.categories,
      location: sPEvent.location,
      iaTargetingOperator: sPEvent.iaTargetingOperator,
      iaTargetingOperatorADGroups: sPEvent.iaTargetingOperatorADGroups,
      iaTargetingTargetContent: sPEvent.iaTargetingTargetContent,
      iaTargetingDepartment: sPEvent.iaTargetingDepartment,
      iaTargetingJobTitle: sPEvent.iaTargetingJobTitle,
      iaTargetingLocation: sPEvent.iaTargetingLocation,
      iaTargetingCustom1: sPEvent.iaTargetingCustom1,
      iaTargetingCustom2: sPEvent.iaTargetingCustom2,
      targetingFields: sPEvent.targetingFields,
      iaTargetingGroupData: sPEvent.iaEventTargetingGroupData,
      targetingExistingStructure: sPEvent.targetingExistingStructure,
      isDraft: sPEvent.draft,
      iaConfiguration: sPEvent.configuration,
      modified: sPEvent.modified,
      feeds: sPEvent.feeds,
      external: sPEvent.external,
      recurrence: sPEvent.recurrence,
      recurrenceData: sPEvent.recurrenceData,
      recurrenceEndDate: sPEvent.recurrenceEndDate,
    };
  }

  private static convertMessageToSPEvent(message: IMessage): ISPEvent {
    return {
      id: message.id ? message.id.toString() : undefined,
      title: message.title,
      content: message.content,
      image: message.image,
      startDate: message.startDate,
      allDayEvent: message.allDayEvent,
      endDate: message.endDate,
      author: message.author,
      likes: message.likes,
      commentCount: message.commentCount,
      categories: message.categories,
      location: message.location,
      iaTargetingOperator: message.iaTargetingOperator,
      iaTargetingOperatorADGroups: message.iaTargetingOperatorADGroups,
      iaTargetingTargetContent: message.iaTargetingTargetContent,
      iaTargetingDepartment: message.iaTargetingDepartment,
      iaTargetingJobTitle: message.iaTargetingJobTitle,
      iaTargetingLocation: message.iaTargetingLocation,
      iaTargetingCustom1: message.iaTargetingCustom1,
      iaTargetingCustom2: message.iaTargetingCustom2,
      targetingFields: message.targetingFields,
      iaEventTargetingGroupData: message.iaTargetingGroupData,
      targetingExistingStructure: message.targetingExistingStructure,
      draft: message.isDraft,
      configuration: message.iaConfiguration,
      modified: message.modified,
      feeds: message.feeds,
      external: message.external,
      recurrence: message.recurrence,
      recurrenceData: message.recurrenceData,
    };
  }

  public static getEvents(environment: IEnvironment, token: string, tenant: string, instance: string, targeting: boolean, filter: IMessageFilter, isFiltered: boolean, showPeriod: IEventPeriod): Promise<IMessageResult> {
    return new Promise((resolve) => {
      if (environment && token && tenant && instance && filter) {
        const url = Environment.getEnvironmentForEventsAPI(environment) + `/${tenant}/${instance}/events?useTargeting=${targeting}`;
        // Check if default filter params are applied. This logic is used on conditional rendering   
        let defaultFilterParams: string[];
        switch (showPeriod) {
          case "upcomming":
          case "fullView":
            defaultFilterParams = ["EndDate", "IADraft", "IADraft"];
            break;
          case "day":
          case "week":
          case "month":
            defaultFilterParams = ["EndDate", "StartDate", "IADraft", "IADraft"];
            break;
          case "all":
            defaultFilterParams = ["IADraft"];
          default:
            break;
        }
        let hasDefaultFilterParams: boolean = isFiltered ? false : true;
        if (!isFiltered) {
          for (let i = 0; i < filter.FilteredBy.length; i++) {
            if (defaultFilterParams && defaultFilterParams.indexOf(filter.FilteredBy[i].fields[0].field) === -1) {
              hasDefaultFilterParams = false;
              break;
            }
          }
        }
        const filterLength: number = (showPeriod === "upcomming" || showPeriod === "fullView") ? 3 : 4;
        const isDefaultFiltered: boolean = !isFiltered && filter.FilteredBy.length === filterLength && hasDefaultFilterParams;

        const body: string = !filter ? null : JSON.stringify(filter);
        new FetchProxy(
          url, {
          method: "POST",
          headers: {
            'AuthToken': token,
            'Content-Type': 'application/json'
          },
          body: body
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              if (response.status === 204) {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: response.statusText
                  }
                });
              } else {
                return response.json();
              }
            }
          }).then(response => {
            const messages = [];
            if (response.result) {
              response.result.forEach((spEvent: ISPEvent) => {
                const message: IMessage = this.convertSPEventToMessage(spEvent);
                if (message.content) {
                  message.content = message.content.replace(/<img[^>]*>/g, "");
                }
                if (message.image && message.image.indexOf(tenant) !== -1) {
                  message.image = Helper.getImageUrl(Environment.getEnvironmentForImageAPI(environment), tenant, token, message.image, message.modified ? message.modified.toString().replace(/[^\d]/g, '') : "", "StandardLarge");
                }
                messages.push(message);
              });
            }
            const messageResult: IMessageResult = {
              pagingInfo: response.pagingInfo,
              messages: messages,
              httpStatus: {
                statusCode: 200
              },
              isDefaultFiltered
            };
            resolve(messageResult);
          }).catch((error) => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getEvent(environment: IEnvironment, token: string, tenant: string, instance: string, messageId: number): Promise<IMessageResult> {
    return new Promise<IMessageResult>((resolve: (result: IMessageResult) => void): void => {
      if (environment && token && tenant && instance && messageId) {
        const apiUrl = Environment.getEnvironmentForEventsAPI(environment) + `/${tenant}/${instance}/events?useTargeting=false`;
        const filter = [];
        filter.push({
          fields: [
            {
              field: "ID",
              type: "Number",
              value: messageId,
              condition: "And",
              operator: "Equal"
            }
          ],
          condition: "And"
        });
        const body: string = JSON.stringify(
          {
            FilteredBy: filter,
            RowLimit: 0,
            PageInfo: undefined,
            OrderedBy: undefined
          }
        );
        new FetchProxy(
          apiUrl, {
          method: "POST",
          headers: {
            'AuthToken': token,
            'Content-Type': 'application/json'
          },
          body: body
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              if (response.status === 404) {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: response.statusText
                  }
                });
              }
              return response.json().then((jsonResponse: any) => {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: jsonResponse.message ? jsonResponse.message : jsonResponse
                  }
                });
              });
            }
            return response.json().then((jsonResponse: any) => {
              const messages = [];
              if (jsonResponse.result) {
                jsonResponse.result.forEach((spEvent: ISPEvent) => {
                  const message: IMessage = this.convertSPEventToMessage(spEvent);
                  if (message.content) {
                    message.content = message.content.replace(/<img[^>]*>/g, "");
                  }
                  if (message.image && message.image.indexOf(tenant) !== -1) {
                    message.image = Helper.getImageUrl(Environment.getEnvironmentForImageAPI(environment), tenant, token, message.image, message.modified ? message.modified.toString().replace(/[^\d]/g, '') : "", "StandardLarge");
                  }
                  messages.push(message);
                });
              }
              const messageResult: IMessageResult = {
                pagingInfo: jsonResponse.pagingInfo,
                messages: messages,
                httpStatus: {
                  statusCode: 200
                }
              };
              resolve(messageResult);
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              pagingInfo: undefined,
              messages: undefined,
              httpStatus: {
                statusCode: 500,
                message: error.message
              }
            });
          });
      } else {
        resolve(null);
      }
    });
  }

  public static createEvent(event: IMessage, environment: IEnvironment, token: string, tenant: string, instance: string): Promise<{ httpStatus: IHttpStatus, id?: string }> {
    return new Promise<{ httpStatus: IHttpStatus, id?: string }>((resolve: (result: { httpStatus: IHttpStatus, id?: string }) => void): void => {
      if (event && environment && token && tenant && instance) {
        const apiUrl = `${Environment.getEnvironmentForEventsAPI(environment)}/${tenant}/${instance}/event`;
        const sPEvent = this.convertMessageToSPEvent(event);
        if (sPEvent.allDayEvent) {
          sPEvent.startDate = `${new DateTimeService().ConvertToYYYYMMDD(sPEvent.startDate)}T00:00:00Z`;
          sPEvent.endDate = `${new DateTimeService().ConvertToYYYYMMDD(sPEvent.endDate)}T00:00:00Z`;
        }
        const body: string = JSON.stringify(sPEvent);
        new FetchProxy(apiUrl, { method: "POST", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: body })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return response.json().then((customEvent: ISPEvent) => {
              resolve({
                httpStatus: {
                  statusCode: 200,
                },
                id: customEvent.id
              });
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              httpStatus: {
                statusCode: error.status,
                message: error.statusText
              }
            });
          });
      } else {
        resolve({
          httpStatus: {
            statusCode: 400,
            message: "Missing parameter"
          }
        });
      }
    });
  }

  public static updateEvent(event: IMessage, environment: IEnvironment, token: string, tenant: string, instance: string): Promise<IHttpStatus> {
    return new Promise<IHttpStatus>((resolve: (httpStatus: IHttpStatus) => void): void => {
      if (event && environment && token && tenant && instance) {
        const sPEvent = this.convertMessageToSPEvent(event);
        const apiUrl = `${Environment.getEnvironmentForEventsAPI(environment)}/${tenant}/${instance}/event/${sPEvent.id}`;
        const body: string = JSON.stringify(sPEvent);
        new FetchProxy(apiUrl, { method: "PUT", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: body })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return resolve({
              statusCode: 200
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              statusCode: error.status,
              message: error.statusText
            });
          });
      } else {
        resolve({
          statusCode: 400,
          message: "Missing parameter"
        })
      }
    });
  }

  public static deleteEvent(id: string, environment: IEnvironment, token: string, tenant: string, instance: string): Promise<string> {
    return new Promise<string>((resolve: (status: string) => void, reject: (error: any) => void): void => {
      if (id && environment && token && tenant && instance) {
        const apiUrl = `${Environment.getEnvironmentForEventsAPI(environment)}/${tenant}/${instance}/event/${id}`;
        new FetchProxy(apiUrl, { method: "DELETE", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return resolve(response.statusText);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static updateTermFields(item: IMessage, environment: IEnvironment, token: string, tenant: string, instance: string, values: any): Promise<string> {
    return new Promise<string>((resolve: (result: string) => void, reject: (error: any) => void): void => {
      if (item && environment && token && tenant && instance && values) {
        if (tenant.indexOf(".sharepoint.com") === -1) {
          tenant = tenant + ".sharepoint.com";
        }
        const apiUrl = `${Environment.getEnvironmentForMainAPI(environment)}/api/targeting/${tenant}/events/${instance}/item?id=${item.id}&targetContent=${item.iaTargetingTargetContent}&targetingOperator=${item.iaTargetingOperator}`;

        const custom1 = [];
        const custom2 = [];
        const department = [];
        const jobTitle = [];
        const location = [];

        if (values.custom1 && values.custom1.length > 0) {
          values.custom1.forEach((_item) => {
            custom1.push(_item.Label ? _item.Label : _item.label);
          });
        }

        if (values.custom2 && values.custom2.length > 0) {
          values.custom2.forEach((_item) => {
            custom2.push(_item.Label ? _item.Label : _item.label);
          });
        }
        if (values.department && values.department.length > 0) {
          values.department.forEach((_item) => {
            department.push(_item.Label ? _item.Label : _item.label);
          });
        }

        if (values.jobTitle && values.jobTitle.length > 0) {
          values.jobTitle.forEach((_item) => {
            jobTitle.push(_item.Label ? _item.Label : _item.label);
          });
        }

        if (values.location && values.location.length > 0) {
          values.location.forEach((_item) => {
            location.push(_item.Label ? _item.Label : _item.label);
          });
        }

        const body: string = JSON.stringify(
          {
            "TargetingCustom1": custom1,
            "TargetingCustom2": custom2,
            "TargetingDepartment": department,
            "TargetingJobTitle": jobTitle,
            "TargetingLocation": location
          }
        );
        new FetchProxy(apiUrl, { method: "POST", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: body })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              console.log("Error updating targeting fields");
              return;
            }
            return resolve("Ok");
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getInstanceSettings = (instance: string, environment, tenant: string, token: string) => {
    const api = `${Environment.getEnvironmentForMainAPI(environment)}/api/instances/getInstance/${tenant}.sharepoint.com/IntraActive-Events/?instanceName=${instance}`;
    return new Promise<any>((resolve: (status: any) => void, reject: (error: any) => void): void => {
      if (environment && token && tenant && instance) {
        new FetchProxy(api, {
          credentials: "same-origin",
          headers: { 'Content-Type': 'application/json', 'AuthToken': token }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json().then(settings => {
              resolve(settings);
            });
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static addExceptionToRecurrence(environment: IEnvironment, tenant: string, instance: string, id: number, exceptionDate: Date) {
    return new Promise<void>((resolve, reject): void => {
      const api = `${Environment.getEnvironmentForEventsAPI(environment)}/${tenant}/${instance}/${id}/recurring-exception?exception=${exceptionDate}`;
      if (environment && tenant && instance && id && exceptionDate) {
        new FetchProxy(api, {
          method: "POST",
          credentials: "same-origin",
          headers: { 'Content-Type': 'application/json' }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            resolve();
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getRecurringEvents(environment: IEnvironment, tenant: string, instance: string) {
    return new Promise<IMessageResult>((resolve, reject): void => {
      const api = `${Environment.getEnvironmentForEventsAPI(environment)}/${tenant}/${instance}/recurring-events`;
      if (environment && tenant && instance) {
        new FetchProxy(api, {
          method: "GET",
          credentials: "same-origin",
          headers: { 'Content-Type': 'application/json' }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              if (response.status === 404) {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: response.statusText
                  }
                });
              }
              return response.json().then((jsonResponse: any) => {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: jsonResponse.message ? jsonResponse.message : jsonResponse
                  }
                });
              });
            }
            return response.json().then((jsonResponse: any) => {
              const messages = [];
              if (jsonResponse.result) {
                jsonResponse.result.forEach((spEvent: ISPEvent) => {
                  const message: IMessage = this.convertSPEventToMessage(spEvent);
                  if (message.content) {
                    message.content = message.content.replace(/<img[^>]*>/g, "");
                  }
                  if (message.image && message.image.indexOf(tenant) !== -1) {
                    message.image = Helper.getImageUrl(Environment.getEnvironmentForImageAPI(environment), tenant, "000", message.image, message.modified ? message.modified.toString().replace(/[^\d]/g, '') : "", "StandardLarge");
                  }
                  messages.push(message);
                });
              }
              const messageResult: IMessageResult = {
                pagingInfo: jsonResponse.pagingInfo,
                messages: messages,
                httpStatus: {
                  statusCode: 200
                }
              };
              resolve(messageResult);
            });

          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }
}